/**
 * useSubtitles Hook
 *
 * SSE connection management for real-time subtitle streaming.
 * Handles connection, reconnection, and subtitle data state.
 */

import { useTranslation } from 'react-i18next';

import { useEffect, useRef, useState, useCallback } from 'react';

export interface SubtitleData {
  stream_key: string;
  text: string;
  is_final: boolean;
  timestamp: number;
  translated_text?: string;
  source_language?: string;
  target_language?: string;
  subtitle_id: string;
}

export interface SSEEvent {
  type: 'connected' | 'subtitle' | 'heartbeat' | 'error';
  data: Record<string, unknown>;
}

export interface UseSubtitlesOptions {
  /** Stream key to subscribe to */
  streamKey: string;
  /** Target language for translation (optional) */
  targetLanguage?: string;
  /** Enable/disable the connection */
  enabled?: boolean;
  /** Auto-reconnect on disconnect */
  autoReconnect?: boolean;
  /** Reconnect delay in milliseconds */
  reconnectDelay?: number;
  /** Maximum number of subtitles to keep in history */
  maxHistory?: number;
}

export interface UseSubtitlesReturn {
  /** Current subtitle text (latest) */
  currentSubtitle: SubtitleData | null;
  /** Subtitle history */
  subtitles: SubtitleData[];
  /** Connection status */
  isConnected: boolean;
  /** Connection error */
  error: string | null;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
  /** Clear subtitle history */
  clearHistory: () => void;
}

export function useSubtitles({
  streamKey,
  targetLanguage,
  enabled = true,
  autoReconnect = true,
  reconnectDelay = 3000,
  maxHistory = 50,
}: UseSubtitlesOptions): UseSubtitlesReturn {
  const [currentSubtitle, setCurrentSubtitle] = useState<SubtitleData | null>(null);
  const [subtitles, setSubtitles] = useState<SubtitleData[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const { t } = useTranslation('watch');
  const [error, setError] = useState<string | { key: string; defaultValue: string } | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const maxReconnectAttempts = 10;
  const subtitleDisplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SUBTITLE_DISPLAY_DURATION = 5000; // 確定字幕の表示時間（5秒）
  // Connection ID to track the current connection attempt and prevent race conditions
  const connectionIdRef = useRef(0);

  const getSSEUrl = useCallback(() => {
    const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
    const baseUrl = backendUrl || window.location.origin;
    const url = new URL(`/api/subtitles/stream/${streamKey}`, baseUrl);

    if (targetLanguage) {
      url.searchParams.set('lang', targetLanguage);
    }

    return url.toString();
  }, [streamKey, targetLanguage]);

  const connect = useCallback(() => {
    // Increment connection ID to invalidate any previous connection's handlers
    connectionIdRef.current++;
    const thisConnectionId = connectionIdRef.current;

    console.log('%c[Subtitles] connect() called', 'background: #9C27B0; color: white; padding: 2px 6px; border-radius: 3px;', { connectionId: thisConnectionId });

    // Cancel any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection BEFORE setting ref to null to prevent race condition
    if (eventSourceRef.current) {
      console.log('[Subtitles] Closing existing connection');
      const oldEventSource = eventSourceRef.current;
      eventSourceRef.current = null; // Clear ref first to prevent onerror from interfering
      oldEventSource.close();
    }

    const url = getSSEUrl();
    console.log('%c[Subtitles] Creating EventSource', 'background: #2196F3; color: white; padding: 2px 6px; border-radius: 3px;', { url, connectionId: thisConnectionId });

    try {
      const eventSource = new EventSource(url);
      console.log('[Subtitles] EventSource created successfully', { connectionId: thisConnectionId });

      // Helper to check if this connection is still valid
      const isValidConnection = () => connectionIdRef.current === thisConnectionId;

      eventSource.addEventListener('connected', (event: MessageEvent) => {
        if (!isValidConnection()) {
          console.log('[Subtitles] Ignoring connected event from stale connection', { thisConnectionId, currentId: connectionIdRef.current });
          return;
        }
        const connData = JSON.parse(event.data);
        console.log('%c[Subtitles] Connected to SSE', 'background: #2196F3; color: white; padding: 2px 6px; border-radius: 3px;', {
          stream_key: connData.stream_key,
          stt_active: connData.stt_active,
          provider: connData.provider,
          subscription_id: connData.subscription_id,
          connectionId: thisConnectionId,
        });
        setIsConnected(true);
        setError(null);
        reconnectAttemptRef.current = 0;
      });

      eventSource.addEventListener('subtitle', (event: MessageEvent) => {
        if (!isValidConnection()) return;
        try {
          const data: SubtitleData = JSON.parse(event.data);
          console.log('%c[Subtitles] Received subtitle', 'background: #4CAF50; color: white; padding: 2px 6px; border-radius: 3px;', {
            text: data.text,
            is_final: data.is_final,
            translated: data.translated_text,
            timestamp: data.timestamp,
          });

          // Clear any existing display timeout
          if (subtitleDisplayTimeoutRef.current) {
            clearTimeout(subtitleDisplayTimeoutRef.current);
            subtitleDisplayTimeoutRef.current = null;
          }

          // Only add final results to history, interim results show as current
          if (data.is_final) {
            setSubtitles((prev) => {
              const newHistory = [...prev, data];
              // Limit history size
              if (newHistory.length > maxHistory) {
                return newHistory.slice(-maxHistory);
              }
              return newHistory;
            });
            // Show final subtitle for a duration before clearing
            setCurrentSubtitle(data);
            subtitleDisplayTimeoutRef.current = setTimeout(() => {
              if (isValidConnection()) {
                setCurrentSubtitle(null);
              }
            }, SUBTITLE_DISPLAY_DURATION);
          } else {
            // Show interim (non-final) results as current subtitle
            setCurrentSubtitle(data);
          }
        } catch (err) {
          console.error('[Subtitles] Failed to parse subtitle:', err);
        }
      });

      eventSource.addEventListener('heartbeat', (event: MessageEvent) => {
        if (!isValidConnection()) return;
        // Heartbeat received, connection is alive
        try {
          const data = JSON.parse(event.data);
          console.log('[Subtitles] Heartbeat received', { provider: data.provider });
        } catch {
          console.log('[Subtitles] Heartbeat received');
        }
      });

      eventSource.addEventListener('provider_changed', (event: MessageEvent) => {
        if (!isValidConnection()) return;
        try {
          const data = JSON.parse(event.data);
          console.log(
            '%c[Subtitles] ★ PROVIDER CHANGED ★',
            'background: #FF9800; color: black; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
            {
              old_provider: data.old_provider,
              new_provider: data.new_provider,
              message: data.message,
            }
          );
        } catch (err) {
          console.error('[Subtitles] Failed to parse provider_changed:', err);
        }
      });

      eventSource.addEventListener('error', (event: MessageEvent) => {
        if (!isValidConnection()) return;
        try {
          const data = JSON.parse(event.data);
          console.error('[Subtitles] Server error:', data.message);
          setError({ key: 'subtitleStatus.serverError', defaultValue: 'Server error' });
        } catch {
          console.error('[Subtitles] Error event received');
        }
      });

      eventSource.onerror = (e) => {
        // Ignore errors from stale connections
        if (!isValidConnection()) {
          console.log('[Subtitles] Ignoring error from stale connection', { thisConnectionId, currentId: connectionIdRef.current });
          eventSource.close();
          return;
        }

        console.log('%c[Subtitles] Connection error/closed', 'background: #F44336; color: white; padding: 2px 6px; border-radius: 3px;', {
          readyState: eventSource.readyState,
          readyStateText: ['CONNECTING', 'OPEN', 'CLOSED'][eventSource.readyState] || 'UNKNOWN',
          error: e,
          connectionId: thisConnectionId,
        });
        setIsConnected(false);
        eventSourceRef.current = null;

        // Auto-reconnect (only if still valid connection)
        if (autoReconnect && reconnectAttemptRef.current < maxReconnectAttempts) {
          reconnectAttemptRef.current++;
          const delay = reconnectDelay * Math.min(reconnectAttemptRef.current, 5);
          console.log(`[Subtitles] Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (isValidConnection()) {
              connect();
            }
          }, delay);
        } else if (reconnectAttemptRef.current >= maxReconnectAttempts) {
          setError({ key: 'subtitleStatus.retryLimit', defaultValue: '再接続の試行回数が上限に達しました' });
        }
      };

      console.log('[Subtitles] EventSource listeners attached, readyState:', eventSource.readyState, { connectionId: thisConnectionId });
      eventSourceRef.current = eventSource;
    } catch (err) {
      console.error('%c[Subtitles] Failed to create EventSource:', 'background: #F44336; color: white; padding: 2px 6px; border-radius: 3px;', err);
      setError({ key: 'subtitleStatus.connectionFailed', defaultValue: '接続に失敗しました' });
    }
  }, [getSSEUrl, autoReconnect, reconnectDelay, maxHistory]);

  const disconnect = useCallback(() => {
    // Increment connection ID to invalidate any pending reconnects or event handlers
    connectionIdRef.current++;
    console.log('[Subtitles] Disconnecting', { newConnectionId: connectionIdRef.current });

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (subtitleDisplayTimeoutRef.current) {
      clearTimeout(subtitleDisplayTimeoutRef.current);
      subtitleDisplayTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptRef.current = 0;
  }, []);

  const reconnect = useCallback(() => {
    console.log('[Subtitles] Manual reconnect');
    reconnectAttemptRef.current = 0;
    setError(null);
    disconnect();
    connect();
  }, [connect, disconnect]);

  const clearHistory = useCallback(() => {
    setSubtitles([]);
    setCurrentSubtitle(null);
  }, []);

  // Track if this effect instance is still "active" to prevent race conditions
  useEffect(() => {
    let isActive = true;

    console.log('%c[Subtitles] useEffect triggered', 'background: #FF5722; color: white; padding: 2px 6px; border-radius: 3px;', {
      enabled,
      streamKey,
      targetLanguage,
      willConnect: enabled && streamKey,
    });

    if (enabled && streamKey) {
      // Clear state for new connection attempt
      setError(null);
      setIsConnected(false);

      // Small delay to ensure cleanup from previous effect has completed
      const connectTimeout = setTimeout(() => {
        if (isActive) {
          connect();
        }
      }, 50);

      return () => {
        isActive = false;
        clearTimeout(connectTimeout);
        disconnect();
      };
    } else {
      // When disabled, just disconnect
      disconnect();
      return () => {
        isActive = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, streamKey, targetLanguage]);

  return {
    currentSubtitle,
    subtitles,
    isConnected,
    error: typeof error === 'string' ? error : error ? t(error.key, error.defaultValue) : null,
    reconnect,
    disconnect,
    clearHistory,
  };
}
